import React, { useEffect, useState } from 'react';
import { type VariantProps } from 'class-variance-authority';
import { cn } from '../../../utils/cn';

import { ReactComponent as APLogoWhite } from '../../../icons/svg/logo-new-white.svg';
import { ReactComponent as APLogoBlack } from '../../../icons/svg/logo-new-black.svg';
import { ReactComponent as APLogoText } from '../../../icons/svg/logo-text-navbar.svg';
import { Button } from '../../../components/button';
import { CategoriesMenu } from './CategoriesMenu';
import { UserCart } from './UserCart';
import {
    _headerVariants,
    HeaderCartInfo,
    type HeaderCountryElement,
    type HeaderNavLinks,
    type HeaderUserData,
} from './Header';
import { WarningBanner } from '../../../ui/warning-banner/components/WarningBanner';
import { DesktopUserMenu } from './user-menu/DesktopUserMenu';
import { RoccoLink } from '../../../components/link/RoccoLink';
import { HeaderSearch, type HeaderSearchTrackingConfig } from './HeaderSearch';
import type { SearchSuggestionResultGroup } from '../../search/view-models/search-suggestion';
import { CountrySelectMenu } from './CountrySelectMenu';

interface DesktopHeaderProps extends VariantProps<typeof _headerVariants> {
    userData: HeaderUserData;
    primaryLinks: HeaderNavLinks;
    currentCountryCode: string;
    countries: HeaderCountryElement[];
    currentUrl: string;
    isSearching?: boolean;
    onInboxClick?: () => void;
    onLogout: () => void;
    onSearchClick?: () => void;
    onNavigate?: (url: string) => void;
    onUserCartClick?: () => void;
    onLoginClick: () => void;
    additionalUserMenuItems?: HeaderNavLinks;
    warningMessages?: string[];
    cartInfo: HeaderCartInfo;
    headerSearchTrackingConfig?: HeaderSearchTrackingConfig;
    fetchSuggestions?: (query: string) => void;
    suggestionData?: SearchSuggestionResultGroup | null;
    uiUpdatesJune2025Enabled?: boolean;
    showTextLogoOnly?: boolean;
    /**
     * Optional location object from Remix Router (pathname, search).
     * Provided when in Remix contexts; omit for non-Remix contexts.
     */
    location?: { pathname: string; search: string } | null;
}

export const DesktopHeader = (props: DesktopHeaderProps) => {
    const {
        color,
        userData,
        primaryLinks,
        currentCountryCode,
        countries,
        currentUrl,
        isSearching,
        onInboxClick,
        onLogout,
        onNavigate,
        onUserCartClick,
        onLoginClick,
        additionalUserMenuItems,
        warningMessages,
        cartInfo,
        uiUpdatesJune2025Enabled,
        headerSearchTrackingConfig,
        fetchSuggestions,
        suggestionData,
        showTextLogoOnly,
        location,
    } = props;

    const [showTextLogo, setShowTextLogo] = useState(!!showTextLogoOnly);
    const [shouldRenderMenu] = useState(true);

    useEffect(() => {
        setShowTextLogo(!!showTextLogoOnly);
    }, [showTextLogoOnly]);

    const mode =
        color === 'transparent' || color === 'none'
            ? uiUpdatesJune2025Enabled &&
              (currentUrl.includes('/app') || currentUrl.includes('/member'))
                ? 'default'
                : 'float'
            : 'default';

    const menuColor =
        color === 'transparent'
            ? uiUpdatesJune2025Enabled &&
              (currentUrl.includes('/app') || currentUrl.includes('/member'))
                ? 'dark'
                : 'light'
            : 'dark';

    const { cartCount } = cartInfo;

    const isAppOrMemberPage =
        currentUrl.includes('/app') || currentUrl.includes('/member');
    const bgClass = isAppOrMemberPage ? 'bg-[#f6f6f6]' : '';

    return (
        <div className={bgClass}>
            <div className="mx-auto w-full qhd:max-w-[1920px]">
                <div
                    className={cn(
                        _headerVariants({ color, mode }),
                        'md:rocco-header z-50 h-48 w-full',
                        uiUpdatesJune2025Enabled &&
                            isAppOrMemberPage && // On member/app pages, give navbar the same bg-color as the page
                            `relative ${bgClass}`, // as new navbar wouldn't have a background on these pages and will appear white.
                        // Keep navbar stationary at top (don't translate on hide)
                        'translate-y-0',
                        'grid grid-cols-[auto_1fr_auto] items-center gap-x-20 px-12 transition-all duration-300 ease-in-out lg:gap-x-16 qhd:mx-auto qhd:max-w-[1920px] qhd:px-12'
                    )}
                >
                    {/* Left Column - Logo */}
                    <div
                        className={cn(
                            'flex h-48 items-center transition-[height] duration-200 ease-in-out'
                        )}
                    >
                        <RoccoLink
                            to="/"
                            prefetch="intent"
                            aria-label="Home"
                            className={cn(
                                color == 'transparent'
                                    ? 'text-gray-50'
                                    : 'text-gray-900',
                                'block w-[40px] sm:w-[50px] md:w-[60px] lg:w-[80px] xl:w-[110px] 2xl:w-[140px]'
                            )}
                        >
                            {showTextLogo ? (
                                <APLogoText
                                    className={cn(
                                        'h-6 w-[85%] animate-left-to-right-fade-in sm:w-auto',
                                        menuColor === 'dark'
                                            ? 'brightness-0'
                                            : 'brightness-0 invert'
                                    )}
                                    style={{
                                        WebkitMaskRepeat: 'no-repeat',
                                        WebkitMaskImage:
                                            'linear-gradient(to right, rgba(0, 0, 0, 1) 85%, rgba(0, 0, 0, 1))',
                                        transform: !showTextLogoOnly
                                            ? 'translateY(0)'
                                            : 'translateY(-1.8rem)',
                                    }}
                                />
                            ) : (
                                <div>
                                    {menuColor === 'dark' ? (
                                        <APLogoBlack className="h-full w-full" />
                                    ) : (
                                        <APLogoWhite className="h-full w-full" />
                                    )}
                                </div>
                            )}
                        </RoccoLink>
                    </div>

                    {/* Second Column - Search and Categories */}
                    <div
                        className={cn(
                            'relative grid h-[65%] w-full grid-rows-[1fr_1fr] items-center transition-all duration-300'
                        )}
                    >
                        {/* Top Row - Search Bar */}
                        <div
                            className={cn(
                                'relative z-30 flex w-full items-center'
                            )}
                        >
                            <HeaderSearch
                                onNavigate={(url) => {
                                    if (onNavigate) {
                                        onNavigate(url);
                                    } else {
                                        window.location.href = url;
                                    }
                                }}
                                displayMode="desktop"
                                placeholderTexts={['Search ArchiPro']}
                                isSearching={isSearching}
                                menuColor={menuColor}
                                trackingConfig={headerSearchTrackingConfig}
                                fetchSuggestions={fetchSuggestions}
                                suggestionData={suggestionData}
                            />
                        </div>

                        {/* Bottom Row - Categories Menu */}
                        {shouldRenderMenu && (
                            <div
                                className={cn(
                                    'relative z-0 -mt-5 flex w-full justify-start pl-6 transition-all duration-300 ease-in-out',
                                    'animate-fade-in-bottom'
                                )}
                            >
                                <CategoriesMenu
                                    color={menuColor}
                                    links={primaryLinks}
                                    currentCountryCode={currentCountryCode}
                                    uiUpdatesJune2025Enabled={
                                        uiUpdatesJune2025Enabled
                                    }
                                />
                            </div>
                        )}
                    </div>

                    {/* Third Column - Buttons and Empty Row */}
                    <div
                        className={cn(
                            'relative grid h-[65%] w-full grid-rows-[1fr_1fr] items-center transition-all duration-300 ease-in-out'
                        )}
                    >
                        {/* Buttons and Country Select Menu */}
                        <div
                            className={cn(
                                'flex items-center justify-end gap-x-10 transition-all duration-300 ease-in-out'
                            )}
                        >
                            <CountrySelectMenu
                                color={menuColor}
                                currentCountryCode={currentCountryCode}
                                countries={countries}
                            />

                            {userData.isAuthorized ? (
                                <>
                                    {cartCount > 0 ? (
                                        <UserCart
                                            fill={
                                                menuColor === 'dark'
                                                    ? 'dark'
                                                    : 'light'
                                            }
                                            onUserCartClick={() => {
                                                onUserCartClick?.();
                                            }}
                                            cartInfo={cartInfo}
                                        />
                                    ) : null}

                                    <DesktopUserMenu
                                        userData={userData}
                                        color={menuColor}
                                        onInboxClick={onInboxClick}
                                        onLogout={onLogout}
                                        currentCountryCode={currentCountryCode}
                                        additionalUserMenuItems={
                                            additionalUserMenuItems
                                        }
                                        location={location}
                                    />
                                </>
                            ) : (
                                <>
                                    {cartCount > 0 ? (
                                        <UserCart
                                            fill={
                                                menuColor === 'dark'
                                                    ? 'dark'
                                                    : 'light'
                                            }
                                            onUserCartClick={() => {
                                                onUserCartClick?.();
                                            }}
                                            cartInfo={cartInfo}
                                        />
                                    ) : null}
                                    <RoccoLink
                                        to={'#'}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            onLoginClick();
                                        }}
                                        aria-label="Log In"
                                        className={cn(
                                            'flex min-w-18 items-center text-base font-medium decoration-2 underline-offset-8 hover:underline md:min-w-14',
                                            color === 'transparent'
                                                ? 'text-gray-50 hover:text-gray-50'
                                                : 'text-gray-900 hover:text-gray-900'
                                        )}
                                    >
                                        Log In
                                    </RoccoLink>
                                    <div>
                                        <Button
                                            color="dark"
                                            weight="default"
                                            size={'default'}
                                            onClick={onLoginClick}
                                            className="min-w-32"
                                        >
                                            Sign Up
                                        </Button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {!!warningMessages?.length && (
                    <div
                        className={cn(
                            'w-[100%] qhd:left-1/2 qhd:max-w-[1920px] qhd:-translate-x-1/2',
                            menuColor === 'light' ||
                                currentUrl.includes('/project/') //TODO: Remove this when new project detail page is up
                                ? 'absolute left-0 top-48 z-40'
                                : 'relative mt-2'
                        )}
                    >
                        <WarningBanner alerts={warningMessages} />
                    </div>
                )}
            </div>
        </div>
    );
};
